import axios from 'axios';
import { ParcelData, LockerAssignment } from '../types';
import { Company } from '@/types/Company';
import { CompanyDepositRequest } from '@/types/CompanyDepositRequest';
import { CompanyDepositResponse } from '@/types/CompanyDepositResponse';
import { ServiceResult } from '@/types/ServiceResult';
import { ShipmentRequest } from '@/types/ShipmentRequest';
import { ShipmentResponse } from '@/types/ShipmentResponse';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:5211/api/';
//process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:5000/api/';
const API_KEY = process.env.REACT_APP_API_KEY ?? 'your_api_key_here';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': API_KEY,
  },
});

export const verifyPhone = async (
  phone: string,
  company: string
): Promise<boolean> => {
  const response = await api.post('/phoneverification/verify-phone', {
    phone,
    company,
  });
  return response.data.isValid;
};

export const sendOTP = async (phone: string): Promise<void> => {
  await api.post('/phoneverification/send-otp', { phone });
};

export const verifyOTP = async (
  phone: string,
  otp: string
): Promise<boolean> => {
  const response = await api.post('/phoneverification/verify-otp', {
    phone,
    otp,
  });
  return response.data.isValid;
};

export const getParcelData = async (barcode: string): Promise<ParcelData> => {
  const response = await api.get(`/parcel/${barcode}`);
  return response.data;
};

export const getParcelDataByBarcode = async (
  barcode: string
): Promise<ParcelData> => {
  const response = await api.get(`/parcel/barcode/${barcode}`);
  return response.data;
};

export const assignLocker = async (
  size: string,
  type: string
): Promise<LockerAssignment> => {
  const response = await api.post('/assign-locker', { size, type });
  return response.data;
};

export const calculateAmount = async (
  size: string,
  type: string,
  holdTime: number
): Promise<number> => {
  const response = await api.post('/calculate-amount', {
    size,
    type,
    holdTime,
  });
  return response.data.amount;
};

export const getCompanies = async (): Promise<Company[]> => {
  const response = await api.get('/companies', {});
  return response.data;
};

export const fetchLockerAssignment = async (
  phone: string
): Promise<LockerAssignment> => {
  const response = await api.get(`/parcel/assignment/${phone}`);
  return response.data;
};

export const confirmPayment = async (
  assignmentId: string
): Promise<boolean> => {
  const response = await api.post('/payment/confirm', { assignmentId });
  return response.data.success;
};

export const completeCollection = async (
  assignmentId: string
): Promise<boolean> => {
  const response = await api.post(`/collect/complete/${assignmentId}`, {});
  return response.data.success;
};

export const createCompanyDeposit = async (
  request: CompanyDepositRequest
): Promise<CompanyDepositResponse> => {
  const response = await api.post<ServiceResult<CompanyDepositResponse>>(
    '/deposit/company',
    request
  );
  if (!response.data.success) {
    throw new Error(
      response.data.error?.code || 'Failed to create company deposit'
    );
  }
  return response.data.data!;
};

export const checkPaymentStatus = async (
  paymentId: string
): Promise<boolean> => {
  const response = await api.get(`/payment/status?paymentId=${paymentId}`);
  return response.data.status === 'completed';
};

export const checkLockerState = async (lockerId: string): Promise<boolean> => {
  try {
    const response = await api.get(`/locker/${lockerId}/state`);
    return response.data.closed;
  } catch (error) {
    console.error('Error checking locker state:', error);
    throw error;
  }
};

export const checkTerminalStatus = async (): Promise<boolean> => {
  const response = await api.get('/paymentTerminal/status');
  return response.data.status === true;
};

export const processTerminalPayment = async (
  amount: number,
  currency: string
): Promise<boolean> => {
  const response = await api.post('/paymentTerminal/process', {
    amount,
    currency,
  });
  return response.data.success === true;
};

export const createShipment = async (
  request: ShipmentRequest
): Promise<ShipmentResponse> => {
  const response = await api.post('/shipment/create', request);
  return response.data;
};

export const getShippingCompanies = async (): Promise<
  Array<{ id: string; name: string }>
> => {
  const response = await api.get('/shipment/companies');
  return response.data;
};

export const confirmShipmentDeposit = async (
  shipmentId: string
): Promise<boolean> => {
  const response = await api.post(`/shipment/${shipmentId}/confirm`);
  return response.data.success;
};

export default api;
